import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const STUDIOS = [
  { id: "paddington", city: "PADDINGTON", address: "64 William Street", suburb: "NSW 2021" },
  { id: "bondi", city: "BONDI-BEACH", address: "177 Glenayr Avenue", suburb: "NSW 2026" },
];

export default function Studios() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });

  // Background subtle parallax (logo watermark)
  const bgY = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);
  const bgScale = useTransform(scrollYProgress, [0, 0.5, 1], [1.1, 1.0, 1.04]);

  // The two columns slide from opposite sides as you scroll into the section
  const leftX = useTransform(scrollYProgress, [0, 0.5], ["-12%", "0%"]);
  const rightX = useTransform(scrollYProgress, [0, 0.5], ["12%", "0%"]);
  const fadeIn = useTransform(scrollYProgress, [0, 0.5], [0, 1]);

  return (
    <section
      ref={ref}
      id="studios"
      data-testid="studios-section"
      className="relative h-screen w-screen overflow-hidden bg-white"
    >
      {/* Full-screen logo watermark (same treatment as hero) */}
      <motion.div
        style={{ y: bgY, scale: bgScale }}
        className="absolute inset-0 pointer-events-none"
      >
        <img
          src="/jay-ink-logo.png"
          alt=""
          aria-hidden
          draggable={false}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 max-w-none object-cover select-none"
          style={{
            width: "max(100vw, 100vh)",
            height: "max(100vw, 100vh)",
            opacity: 1,
          }}
        />
      </motion.div>
      {/* Soft white veil for text legibility */}
      <div className="absolute inset-0 bg-white/60 pointer-events-none" />

      {/* Top labels */}
      <div className="absolute top-24 md:top-28 left-6 md:left-12 right-6 md:right-12 flex justify-between items-center text-[#0A0A0A]/70 z-10">
        <span className="text-[10px] md:text-xs tracking-[0.4em] uppercase">— Studios</span>
        <span className="hidden md:inline text-[10px] tracking-[0.4em] uppercase">Sydney, AU</span>
      </div>

      {/* Center grid: two cities side by side */}
      <div className="relative z-10 h-full w-full flex items-center justify-center px-6 md:px-12">
        <div className="w-full max-w-[1600px] grid grid-cols-1 md:grid-cols-2 gap-16 md:gap-8">
          {STUDIOS.map((s, i) => {
            const x = i === 0 ? leftX : rightX;
            return (
              <motion.div
                key={s.id}
                data-testid={`studio-card-${s.id}`}
                style={{ x, opacity: fadeIn }}
                className="text-center group"
              >
                <h2
                  className="text-[#0A0A0A] uppercase leading-none whitespace-nowrap"
                  style={{
                    fontFamily: "'Jost', 'Futura', 'Helvetica Neue', sans-serif",
                    fontSize: "clamp(1.6rem, 4.2vw, 4rem)",
                    letterSpacing: "0.18em",
                    paddingLeft: "0.18em",
                    fontWeight: 200,
                  }}
                >
                  {s.city}
                </h2>
                <div className="mt-6 md:mt-10 mx-auto w-12 h-px bg-[#0A0A0A]/30 transition-all duration-700 group-hover:w-24 group-hover:bg-[#0A0A0A]" />
                <p
                  className="mt-6 md:mt-10 text-[#0A0A0A] uppercase"
                  style={{
                    fontFamily: "'Jost', 'Futura', 'Helvetica Neue', sans-serif",
                    fontSize: "clamp(0.78rem, 1.3vw, 1.15rem)",
                    letterSpacing: "0.32em",
                    paddingLeft: "0.32em",
                    fontWeight: 300,
                  }}
                >
                  {s.address}
                </p>
                <p className="mt-3 text-[10px] md:text-xs tracking-[0.3em] uppercase text-[#666]">
                  {s.suburb}
                </p>
                {!s.openingSoon && (
                  <a
                    href="#location"
                    data-testid={`studio-directions-${s.id}`}
                    className="inline-block mt-8 md:mt-10 text-[10px] md:text-xs tracking-[0.32em] uppercase text-[#0A0A0A] border-b border-[#0A0A0A] pb-1 hover:opacity-60 transition-opacity"
                  >
                    View on map →
                  </a>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Bottom decorative line */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 text-[10px] tracking-[0.4em] uppercase text-[#0A0A0A]/45 z-10">
      </div>
    </section>
  );
}
